import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../services/web/bos-service.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {subMonths} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subMonths(new Date(), 1),
    dtEnd: new Date(),
    prefix: [],
    fuel_type: [],
    gas_station: [],
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    prefix: "Prefixo do veículo",
    fuel_type: "Tipo do combustível",
    gas_station: "Posto de combustível"
};

const FleetSupplies = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);

    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [filters, setFilters] = useState(initialFilters);
    const buttonRef = useRef(null);

    const title = "Abastecimento";
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : ""
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                prefix: filters.prefix?.map(i => i.value) ?? [],
                fuel_type: filters.fuel_type?.map(i => i.value) ?? [],
                gas_station: filters.gas_station?.map(i => i.value) ?? []
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd},
            filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/fleet-supplies/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;
            setReportData(data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setLoading(false);
        }
    }

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(initialFilters);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={reportData?.supplyGraphs?.getGasTypes?.length}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                                setCollapsed={setCollapsed}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || !reportData?.supplyGraphs?.getGasTypes ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || !reportData?.supplyGraphs?.getGasTypes ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || !reportData?.supplyGraphs?.getGasTypes ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={!reportData?.supplyGraphs?.getGasTypes && 0}/>
                            <Content reportData={reportData} data={reportData?.supplyGraphs?.getGasTypes?.length} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </SubScroll>
    );
};

export default FleetSupplies;